import {useTranslation} from "react-i18next";

import {
    ENERGY_SCALE_BOUNDARIES,
    ENERGY_SCALE_INTERVALS,
    ENERGY_SCALE_MAX,
    ENERGY_SCALE_MIN,
    getEnergyScalePositionPercent,
    isEnergyDemand,
} from "@reva/shared/energy-scale";

import {useSelectByCurrentLanguage} from "../../../hooks/languageHooks";

/**
 * Energy scale rendered in TabObjectEnergy
 * @return {JSX.Element}
 */
export function EnergyScale({energy_value, energy_value_primary, energy_type}) {
    const selectLang = useSelectByCurrentLanguage();
    const {t} = useTranslation();
    if (!energy_value) return null;
    const formatter = (value) => {
        return selectLang(`${value.valueDe} ${value.unitDe}`, `${value.valueEn} ${value.unitEn}`);
    };
    const minValue = ENERGY_SCALE_MIN;
    const maxValue = ENERGY_SCALE_MAX;
    const intervals = ENERGY_SCALE_INTERVALS;
    const boundaries = ENERGY_SCALE_BOUNDARIES;

    const isDemand = isEnergyDemand(energy_type?.value);
    const finalEnergyLabel = isDemand
        ? t("object.energy.output.scale.final_energy_demand")
        : t("object.energy.output.scale.final_energy_consumption");
    const primaryEnergyLabel = isDemand
        ? t("object.energy.output.scale.primary_energy_demand")
        : t("object.energy.output.scale.primary_energy_consumption");

    // Marker Position setzen (0-250)
    function getMarkerPosition(input) {
        if (!input) return "0%";
        return `${getEnergyScalePositionPercent(input.value)}%`;
    }

    return (
        <div className="energy-scale-container">
            <div className="energy-scale">
                {/* Intervall-Beschriftungen auf der Skala */}
                <div className="energy-scale-intervals">
                    {intervals.map(({label, start, end}) => {
                        const left = ((start - minValue) / (maxValue - minValue)) * 100;
                        const width = ((end - start) / (maxValue - minValue)) * 100;
                        return (
                            <div
                                key={`${label}-${start}-${end}`}
                                className="energy-scale-interval"
                                style={{left: `${left}%`, width: `${width}%`}}
                            >
                                <span className="energy-scale-interval-label">{label}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Vertikale Trenner an Intervallgrenzen */}
                <div className="energy-scale-separators">
                    {boundaries.map((b, i) => {
                        const left = ((b - minValue) / (maxValue - minValue)) * 100;
                        return (
                            <div key={`sep-${i}-${b}`} className="energy-scale-separator" style={{left: `${left}%`}} />
                        );
                    })}
                </div>

                {/* Marker */}
                <div className="energy-scale-value-marker-label" style={{left: getMarkerPosition(energy_value)}}>
                    {finalEnergyLabel}
                    <br />
                    {formatter(energy_value)}
                </div>
                <div className="energy-scale-value-marker" style={{left: getMarkerPosition(energy_value)}}></div>
                {energy_value_primary && (
                    <>
                        <div
                            className="energy-scale-primary-energy-marker"
                            style={{left: getMarkerPosition(energy_value_primary)}}
                        ></div>
                        <div
                            className="energy-scale-primary-energy-marker-label"
                            style={{left: getMarkerPosition(energy_value_primary)}}
                        >
                            {primaryEnergyLabel}
                            <br />
                            {formatter(energy_value_primary)}
                        </div>
                    </>
                )}
            </div>

            <div className="energy-scale-labels" style={{position: "relative"}}>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((0 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    0
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((25 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    25
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((50 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    50
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((75 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    75
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((100 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    100
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((125 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    125
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((150 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    150
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((175 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    175
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((200 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    200
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((225 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    225
                </div>
                <div
                    className="energy-scale-label"
                    style={{
                        position: "absolute",
                        left: `${((250 - minValue) / (maxValue - minValue)) * 100}%`,
                        transform: "translateX(-50%)",
                    }}
                >
                    {">"}250
                </div>
            </div>
        </div>
    );
}
